import React, { useRef, useEffect } from 'react'
import styled from '@emotion/styled'
import { css } from '@emotion/core'
import Toggle from 'react-toggled'

import { Power3 } from '../lib/gsap'

import useScrolledBelow from '../hooks/useScrolledBelow'
import { useTimeline } from '../hooks/animation'

import { useNavbarTheme } from '../contexts/NavbarTheme'

import Logo from './Logo'
import AdVictoremLogo from './AdVictoremLogo'
import CurrentPageLabel from './CurrentPageLabel'
import Hamburger from './Hamburger'
import Link from './Link'
import ContentColumn from './ContentColumn'
import SocialIcons from './SocialIcons'
import Play from './Play'
import LinesCurtain from './LinesCurtain'
import Space from './Space'
import { WideText } from '../styles/typography'

import theme from '../styles/theme'
import { rem, media, min, lines } from '../styles/tools'

import click18 from '../assets/sounds/click_18.wav'

const NavbarWrapper = styled.div`
  ${p => p.height && `height: ${p.height}`};
  color: #fff;
  background-color: ${theme.colors.grey_1100};
  transition: 0.5s background-color, 0.5s color;

  ${media.laptop} {
    background-color: ${p =>
      p.transparentBackground ? 'transparent' : theme.colors.grey_1000};
    ${p => {
      if (p.transparentBackground && p.delay)
        return `
          transition-duration: 0s, .5s;
          transition-delay: 0s;
        `
      if (p.delay) return `transition-delay: .5s, 0s;`
    }};

    ${p => p.darkText && `color: #000`};
  }
`

const MobileNavLink = props => (
  <WideText
    as={Link}
    css={{
      width: '100%',
      fontSize: rem(24),
      textAlign: 'center',
      WebkitTapHighlightColor: 'transparent',
    }}
    {...props}
  />
)

MobileNavLink.defaultProps = {
  activeClassName: 'active',
}

const DesktopNavLink = props => (
  <Link
    css={css`
      display: inline-block;
      position: relative;
      width: 100%;
      padding: ${rem(theme.space.lg)} ${rem(20)};
      ${media.laptopLg} {
        padding: ${rem(theme.space.lg)} ${rem(30)};
      }
      letter-spacing: 0.2em;
      font-weight: normal;
      font-family: TungstenMedium, sans-serif;
      text-transform: uppercase;
      white-space: nowrap;
      -webkit-tap-highlight-color: transparent;

      font-size: 24px;
      ${media.tabletMd} {
        font-size: 16px;
      }

      &::before {
        content: '';
        display: block;
        width: 100%;
        height: ${rem(6)};
        position: absolute;
        top: 0;
        left: 0;
        opacity: 0;
        transform: scaleY(0);
        transform-origin: center top;
        transition-property: transform, opacity, background-color;
        transition-duration: 0.5s;
        will-change: transform, opacity;
      }

      .theme--light &::before {
        background-color: ${theme.colors.grey_1100};
      }

      .theme--dark &::before {
        background-color: #fff;
      }

      &.active::before,
      &:hover::before,
      &:focus::before {
        opacity: 1;
        transform: scaleY(1);
      }

      > span {
        display: none;
        ${min(1220)} {
          display: block;
        }
        margin-top: ${rem(4)};
        margin-bottom: ${rem(-12)};
        font-size: 60%;
        ${min(1485)} {
          font-size: 80%;
        }
        ${min(1545)} {
          font-size: 90%;
        }
        ${min(1660)} {
          font-size: 100%;
        }
        white-space: normal;
        opacity: 0;
        transition: opacity 0.5s;
      }

      &:hover > span {
        opacity: 0.5;
      }

      > * {
        pointer-events: none;
      }
    `}
    activeClassName="active"
    {...props}
  />
)

const MobileNavButton = styled.button(p => ({
  height: p.height,
  padding: theme.space.md,
  WebkitTapHighlightColor: 'transparent',
}))

const MobileNavLabel = styled.span`
  display: inline-block;
  margin-top: 2px;
  letter-spacing: 0.2em;
  font-weight: normal;
  font-family: TungstenMedium, sans-serif;
  text-transform: uppercase;
  font-size: 13px;
  ${media.tabletMd} {
    font-size: 16px;
  }
  line-height: 1;
  opacity: 0;
  visibility: hidden;
  ${p =>
    p.active &&
    `opacity: 1;
     visibility: visible;`};
  transition-property: visibility, opacity;
  transition-timing-function: ease-out;
  transition-duration: 0.5s;
`

const MobileNavbar = props => (
  <Toggle>
    {({ on, getTogglerProps, setOff }) => (
      <div
        css={{ display: 'flex', alignItems: 'center', height: '100%' }}
        {...props}
      >
        <Space x={10} />
        <Link to="/" css={{ position: 'relative', zIndex: 1 }}>
          <Logo width={rem(33)} onClick={setOff} />
        </Link>

        <MobileNavButton
          {...getTogglerProps()}
          css={{
            height: rem(theme.navbarHeight.mobile),
            [media.laptop]: { height: theme.navbarHeight.laptop },
            position: 'relative',
            zIndex: 1,
            marginLeft: 'auto',
          }}
        >
          <div css={{ display: 'flex', alignItems: 'center' }}>
            <MobileNavLabel active={!on}>
              <CurrentPageLabel />
            </MobileNavLabel>
            <Space x={6} />
            <Hamburger active={on} />
          </div>
        </MobileNavButton>

        <LinesCurtain active={on} />

        <div
          css={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            height: '100vh',
            overflow: 'auto',
            position: 'absolute',
            top: 0,
            left: 0,
            opacity: 0,
            visibility: 'hidden',
            backgroundColor: theme.colors.grey_1100,
            transitionProperty: 'visibility, opacity',
            transitionTimingFunction: 'ease-out',
            transitionDuration: '0.5s',
          }}
          style={
            on
              ? {
                  opacity: 1,
                  visibility: 'visible',
                  transitionDelay: '.5s',
                  transitionDuration: '1s',
                }
              : {}
          }
        >
          <div css={{ padding: rem(19) }}>
            <AdVictoremLogo />
          </div>
          <ul
            css={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              alignItems: 'center',
              height: '65vh',
              padding: '5vh 0',
            }}
            onClick={setOff}
          >
            <li>
              <MobileNavLink to="/">WELCOME</MobileNavLink>
            </li>
            <li>
              <MobileNavLink partial to="/work">
                OUR WORK
              </MobileNavLink>
            </li>
            <li>
              <MobileNavLink partial to="/agency">
                OUR AGENCY
              </MobileNavLink>
            </li>
            <li>
              <MobileNavLink to="/playbook">OUR PLAYBOOK</MobileNavLink>
            </li>
            <li>
              <MobileNavLink partial to="/blog">
                OUR BLOG
              </MobileNavLink>
            </li>
            <li>
              <MobileNavLink to="/contact">YOUR TURN</MobileNavLink>
            </li>
          </ul>
          <SocialIcons css={{ padding: `${rem(20)} 0` }} />
        </div>
      </div>
    )}
  </Toggle>
)

const DesktopNavbar = props => {
  const tl = useTimeline()
  const ref = useRef()

  useEffect(() => {
    tl.staggerFrom(
      ref.current.querySelectorAll('li > a'),
      1,
      {
        x: '-100%',
        opacity: 0,
        ease: Power3.easeInOut,
        delay: 0.5,
      },
      -0.07
    )
  }, [])

  return (
    <Play sounds={{ playOnClick: click18 }}>
      {({ playOnClick }) => (
        <div ref={ref} {...props}>
          <ContentColumn
            as="ul"
            css={{
              display: 'flex',
              '> li': { flex: '1 0', overflow: 'hidden' },
            }}
          >
            <li>
              <DesktopNavLink to="/" onClick={playOnClick}>
                WELCOME
              </DesktopNavLink>
            </li>
            <li>
              <DesktopNavLink partial to="/work" onClick={playOnClick}>
                OUR WORK
                <span>CLIENTS + CASE STUDIES</span>
              </DesktopNavLink>
            </li>
            <li>
              <DesktopNavLink partial to="/agency" onClick={playOnClick}>
                OUR AGENCY
                <span>PRINCIPALS + PRINCIPLES</span>
              </DesktopNavLink>
            </li>
            <li>
              <DesktopNavLink to="/playbook" onClick={playOnClick}>
                OUR PLAYBOOK
                <span>SERVICES + PROCESS</span>
              </DesktopNavLink>
            </li>
            <li>
              <DesktopNavLink partial to="/blog" onClick={playOnClick}>
                OUR BLOG
                <span>NEWS + VIEWS</span>
              </DesktopNavLink>
            </li>
            <li>
              <DesktopNavLink to="/contact" onClick={playOnClick}>
                YOUR TURN
                <span>Work With Us</span>
              </DesktopNavLink>
            </li>
          </ContentColumn>
        </div>
      )}
    </Play>
  )
}

const Navbar = () => {
  const isBelow = useScrolledBelow(20)
  const { theme: navbarTheme, delay } = useNavbarTheme()

  return (
    <NavbarWrapper
      as="nav"
      darkText={navbarTheme === 'light' && !isBelow}
      delay={delay}
      className={
        navbarTheme === 'dark-solid'
          ? 'theme--dark'
          : navbarTheme === 'light' && !isBelow
          ? 'theme--light'
          : 'theme--dark'
      }
      transparentBackground={navbarTheme === 'dark-solid' ? false : !isBelow}
      css={{
        height: rem(theme.navbarHeight.mobile),
        [media.laptop]: { height: rem(theme.navbarHeight.laptop) },
      }}
    >
      <div
        css={{
          [media.max.laptop]: { display: 'none' },
          position: 'absolute',
          top: 0,
          right: theme.barWidth.laptop,
          left: 0,
          bottom: 0,
          opacity: navbarTheme === 'dark-solid' || isBelow ? 0.2 : 0,
          transition: 'opacity 1s',
          ...lines('#666'),
          willChange: 'opacity',
        }}
      />
      <MobileNavbar css={{ [media.laptop]: { display: 'none' } }} />
      <DesktopNavbar css={{ [media.max.laptop]: { display: 'none' } }} />
    </NavbarWrapper>
  )
}

export default Navbar
